import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { OperationType, DataAreaType, Endpoint, HelpIndex, MessageType } from '../../../models/enums';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { ConnectorsAndTreeComponent } from '../../connectors-and-tree/connectors-and-tree.component';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { DrObject, DrObjectType, SupportedRegExType } from '../../../models/dr-object';
import { DataArea } from '../../../models/data-area';
import { isUndefined } from '../../../models/utils';

@Component({
    standalone: false,
    selector: 'res-data-area-modal',
    templateUrl: './data-area-modal.component.html',
    styleUrls: ['./data-area-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DataAreaModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild(ConnectorsAndTreeComponent, { static: true }) connectorsAndTreeComponent: ConnectorsAndTreeComponent;

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (name) => {
            return {
                name: this.nameDescriptionComponent.name,
                objectType: this.nameDescriptionComponent.drObject.objectType,
                connectorHandle: this.connectorsAndTreeComponent.selectedConnector.handle
            };
        }
    };

    modalTitle = '';
    modalAutomationId = 'data-area';
    verifyRequiredValidator = true;
    primaryButtonLabel = '';
    secondaryButtonLabel = 'Cancel';
    customClassRow = 'inline';
    modalHelpIndex: HelpIndex;
    isBusy = false;
    isBusyMessage = '';
    isEditDataArea = false;
    dataAreaOperationType: OperationType;
    updateStatusType = MessageType.dataArea;
    folderDirty = false;
    selectedOriginalPath: string;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.dataAreaOperationType = <OperationType>this.modalData.operationType;
        if (this.modalData.dataAreaType === DataAreaType.PROJECT) {
            this.modalTitle = this.dataAreaOperationType === OperationType.new ? 'New Project Data Area' : 'Edit Project Data Area';
            this.nameDescriptionComponent.drObject = new DrObject(DrObjectType.PROJECT_DATA_AREA, SupportedRegExType.DATAAREA);
        } else {
            this.modalTitle = this.dataAreaOperationType === OperationType.new ? 'New Export Data Area' : 'Edit Export Data Area';
            this.nameDescriptionComponent.drObject = new DrObject(DrObjectType.EXPORT_DATA_AREA, SupportedRegExType.DATAAREA);
        }

        this.nameDescriptionComponent.drObject.maxNameLength = DataArea.maxNameLength;

        if (this.dataAreaOperationType === OperationType.edit) {
            this.folderDirty = false;
            this.isEditDataArea = true;
            this.nameDescriptionComponent.name = this.modalData.dataAreaItem.name;
            if (!isUndefined(this.modalData.dataAreaItem.description)) {
                this.nameDescriptionComponent.description = this.modalData.dataAreaItem.description;
            }
            this.connectorsAndTreeComponent.selectedPath = this.selectedOriginalPath = this.connectorsAndTreeComponent.selectedOriginalPath = this.modalData.dataAreaItem.path;
            this.connectorsAndTreeComponent.dataAreaConnectorHandle = this.modalData.dataAreaItem.connectorHandle;
        }
        this.connectorsAndTreeComponent.dataAreaType = this.modalData.dataAreaType;
        this.connectorsAndTreeComponent.dataAreaOperationType = this.dataAreaOperationType;
        this.primaryButtonLabel = this.dataAreaOperationType === OperationType.new ? 'Create Data Area' : 'Save';
        this.setHelpIndex();
    }

    setHelpIndex(): void {
        this.modalHelpIndex = this.modalData.dataAreaType === DataAreaType.PROJECT ? HelpIndex.add_project_area_dlg : HelpIndex.add_export_data_area_dlg;
    }

    isFolderSelectionComplete(): boolean {
        let isFolderSelectionComplete = false;
        if (!isUndefined(this.connectorsAndTreeComponent.selectedConnector) && !isUndefined(this.connectorsAndTreeComponent.selectedPath) &&
            (this.connectorsAndTreeComponent.selectedPath !== '') && !isUndefined(this.connectorsAndTreeComponent.selectedOriginalPath)) {
            isFolderSelectionComplete = true;
        }
        return isFolderSelectionComplete;
    }

    isPrimaryButtonEnabled(): boolean {
        let enabled = false;
        if (!this.isBusy) {
            if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && !isUndefined(this.connectorsAndTreeComponent.selectedConnector)) {
                if (this.nameDescriptionComponent.isNameDirty() && (this.nameDescriptionComponent.formValid) && !this.folderDirty) {
                    enabled = true;
                } else if ((this.nameDescriptionComponent.isDescriptionDirty()) && (this.nameDescriptionComponent.formValid) && this.isFolderSelectionComplete()) {
                    enabled = true;
                } else if (this.isFolderSelectionComplete() && this.folderDirty) {
                    enabled = true;
                }
            }
        }
        return enabled;
    }

    setPath(selPath: any): void {
        this.folderDirty = false;
        if (DrObject.isValueDirty(this.connectorsAndTreeComponent.selectedFolder.path, this.connectorsAndTreeComponent.selectedOriginalPath)) {
            this.folderDirty = true;
        }
    }

    onConnectorAndTreeChange(): void {
        if (this.dataAreaOperationType === OperationType.edit) {
            this.folderDirty = false;
            if (!isUndefined(this.selectedOriginalPath) && !isUndefined(this.connectorsAndTreeComponent.selectedFolder) &&
                (this.connectorsAndTreeComponent.selectedFolder != null) &&
                // should be !isUndefined(this.connectorsAndTreeComponent.selectedFolder.path) &&
                !isUndefined(this.connectorsAndTreeComponent.selectedFolder.handle) &&
                DrObject.isValueDirty(this.connectorsAndTreeComponent.selectedFolder.handle, this.selectedOriginalPath)) {
                this.folderDirty = true;
            }
        }
    }

    createEditDataArea(): void {
        if (this.dataAreaOperationType === OperationType.new) {
            this.nameDescriptionComponent.validate().then(valid => {
                if (valid) {
                    this.isBusy = true;
                    this.isBusyMessage = 'Creating Data Area ...';
                    let selectedPath = this.connectorsAndTreeComponent.selectedFolder ? this.connectorsAndTreeComponent.selectedFolder.handle :
                        this.connectorsAndTreeComponent.selectedConnector.remotePath;
                    if (selectedPath.includes(this.connectorsAndTreeComponent.selectedConnector.remotePath)) {
                        selectedPath = selectedPath.replace(this.connectorsAndTreeComponent.selectedConnector.remotePath, '');
                    }
                    const request = {
                        name: this.nameDescriptionComponent.name,
                        description: this.nameDescriptionComponent.description,
                        mode: this.modalData.dataAreaType === DataAreaType.PROJECT ? 'PROJECT' : 'EXPORT',
                        connectorHandle: this.connectorsAndTreeComponent.selectedConnector.handle,
                        path: selectedPath,
                        errorIfExists: true,
                        skippedDirectories: [],
                    };
                    this.restService.post(Endpoint.orgManager_createDataArea, request).then(response => {
                        this.isBusy = false;
                        this.closeModal(true, response);
                        this.commonService.sendMessage(MessageType.dataArea, this.updateStatusType);
                        this.folderDirty = false;
                    }, error => {
                        this.isBusy = false;
                    });
                }
            });
        } else if (this.dataAreaOperationType === OperationType.edit) {
            if (this.nameDescriptionComponent.isNameDirty() || this.nameDescriptionComponent.isDescriptionDirty()) {
                this.nameDescriptionComponent.validate().then(valid => {
                    if (valid) {
                        this.saveEdits();
                    }
                });
            } else if (this.folderDirty === true) {
                this.saveEdits();
            }
        }
    }

    saveEdits(): void {
        if (this.dataAreaOperationType === OperationType.edit) {
            this.isBusy = true;
            this.isBusyMessage = 'Updating Data Area ...';
            let selectedPath = this.connectorsAndTreeComponent.selectedFolder ? this.connectorsAndTreeComponent.selectedFolder.handle :
                this.connectorsAndTreeComponent.selectedConnector.remotePath;
            if (selectedPath.includes(this.connectorsAndTreeComponent.selectedConnector.remotePath)) {
                selectedPath = selectedPath.replace(this.connectorsAndTreeComponent.selectedConnector.remotePath, '');
            }
            const request = {
                name: this.nameDescriptionComponent.name,
                description: this.nameDescriptionComponent.description,
                mode: this.modalData.dataAreaType === DataAreaType.PROJECT ? 'PROJECT' : 'EXPORT',
                connectorHandle: this.connectorsAndTreeComponent.selectedConnector.handle,
                path: selectedPath,
                handle: this.modalData.dataAreaItem.handle
            };
            this.restService.post(Endpoint.dataAreaManager_updateDataArea, request).then(response => {
                this.isBusy = false;
                this.closeModal(true, response);
                this.commonService.sendMessage(MessageType.dataArea, this.updateStatusType);
                this.folderDirty = false;
            }, error => {
                this.isBusy = false;
            });
        }
    }

    onPathChanged(): void {
        this.connectorsAndTreeComponent.selectedPath = this.connectorsAndTreeComponent.selectedPath.replace(new RegExp(/\\/g), '/');
    }

    clearTreeSelection(): void {
        this.connectorsAndTreeComponent.selectedNode = null;
        this.connectorsAndTreeComponent.tree.selectedNode = null;
    }
}
